import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { initPdfWorker } from '../../utils/pdfWorker';

// Initialize PDF worker
initPdfWorker();

// Define options for PDF rendering - defined outside component to avoid re-creation
const options = {
  cMapUrl: 'https://unpkg.com/pdfjs-dist/cmaps/',
  standardFontDataUrl: 'https://unpkg.com/pdfjs-dist/standard_fonts',
};

interface PDFViewerProps {
  url: string;
  pageNumber?: number;
}

// Create a type for the methods we want to expose
export interface PDFViewerHandle {
  previousPage: () => void;
  nextPage: () => void;
  getCurrentPage: () => number;
}

const PDFViewer = forwardRef<PDFViewerHandle, PDFViewerProps>(({ url, pageNumber = 1 }, ref) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(pageNumber);
  const [scale, setScale] = useState<number>(1.35);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [useEmbedViewer, setUseEmbedViewer] = useState(false);

  // Add debug logging for PDF URL
  console.log('PDFViewer: Attempting to load PDF from URL:', url);
  console.log('PDFViewer: Starting at page:', pageNumber);

  // Function to get an embedded PDF viewer for Azure Blob Storage
  const getEmbeddedPdfViewer = () => {
    if (url.includes('publicstoragea08486eb.blob.core.windows.net')) {
      return (
        <div className="w-full h-full flex flex-col">
          <div className="bg-gray-100 p-4 rounded mb-4">
            <p className="text-gray-500 text-sm">Displaying PDF using embedded viewer</p>
          </div>
          <iframe
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(url)}${pageNumber > 1 ? `&page=${pageNumber-1}` : ''}&embedded=true`}
            className="w-full flex-1 border-none min-h-[500px]"
            title="PDF Viewer"
          />
        </div>
      );
    }
    return null;
  };

  // Reset when URL changes
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    setCurrentPage(pageNumber);

    // Check if we should use the embedded viewer for Azure Blob Storage
    if (url.includes('publicstoragea08486eb.blob.core.windows.net')) {
      // For Azure Storage PDFs, use direct iframe embedding which is more reliable
      setUseEmbedViewer(true);
      setIsLoading(false);
    } else {
      setUseEmbedViewer(false);
    }
  }, [url, pageNumber]);

  // Update container width on resize
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    // Initial width
    updateContainerWidth();

    // Update on window resize
    window.addEventListener('resize', updateContainerWidth);
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, []);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
    setIsLoading(false);
    setError(null);

    // Ensure page number is valid
    if (currentPage > numPages) {
      setCurrentPage(1);
    }
  }

  function onDocumentLoadError(error: Error): void {
    console.error('Error loading PDF:', error);
    setError(`Failed to load PDF: ${error.message}`);

    // For Azure Blob Storage URLs, switch to embedded viewer on error
    if (url.includes('publicstoragea08486eb.blob.core.windows.net')) {
      setUseEmbedViewer(true);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }

  function changePage(offset: number) {
    setCurrentPage(prevPageNumber => {
      const newPage = prevPageNumber + offset;
      return Math.max(1, Math.min(numPages || 1, newPage));
    });
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  function zoomIn() {
    setScale(prevScale => Math.min(2.5, prevScale + 0.1));
  }

  function zoomOut() {
    setScale(prevScale => Math.max(0.5, prevScale - 0.1));
  }

  // Expose methods to parent components through the ref
  useImperativeHandle(ref, () => ({
    previousPage,
    nextPage,
    getCurrentPage: () => currentPage
  }));

  // Use direct iframe embedding for Azure Blob Storage PDFs
  if (useEmbedViewer) {
    return (
      <div className="pdf-viewer-container flex flex-col h-full">
        <div className="flex-1 overflow-hidden" style={{ minHeight: "70vh", height: "70vh" }}>
          <iframe
            src={`${url}${pageNumber > 1 ? `#page=${pageNumber}` : ''}`}
            style={{ width: '100%', height: '100%', minHeight: '70vh', border: 'none' }}
            title="PDF Viewer"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-viewer-container flex flex-col h-full">
      {error ? (
        <div className="flex items-center justify-center h-full p-8 text-center">
          <div className="bg-red-500/20 text-red-400 p-4 rounded-lg">
            <p>{error}</p>
            <p className="text-sm mt-2">URL: {url}</p>
            <div className="mt-4">
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Open PDF in new tab
              </a>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div ref={containerRef} className="flex-1 overflow-auto bg-gray-800 rounded-lg flex justify-center h-full">
            <div className="pdf-document-container max-w-full p-4 h-full flex items-start justify-center">
              <Document
                file={url}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                options={options}
                loading={
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin h-10 w-10 border-4 border-accent border-t-transparent rounded-full"></div>
                    <p className="ml-3 text-gray-400">Loading PDF...</p>
                  </div>
                }
                error={
                  <div className="flex items-center justify-center h-64 text-red-500">
                    <p>Could not load PDF. Please check the URL and try again.</p>
                  </div>
                }
                noData={
                  <div className="flex items-center justify-center h-64 text-gray-400">
                    <p>No PDF file specified.</p>
                  </div>
                }
                className="flex justify-center"
              >
                {numPages && (
                  <div className="flex justify-center">
                    <Page
                      pageNumber={currentPage}
                      scale={scale}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      width={containerWidth ? Math.min(containerWidth - 64, 900) : undefined}
                      className="pdf-page"
                    />
                  </div>
                )}
              </Document>
            </div>
          </div>

          {numPages && (
            <div className="flex items-center justify-between mt-4 p-2 bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-2">
                <button
                  onClick={zoomOut}
                  className="p-2 rounded-md hover:bg-gray-600 transition-colors"
                  aria-label="Zoom out"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path fillRule="evenodd" d="M10.5 3.75a6.75 6.75 0 100 13.5 6.75 6.75 0 000-13.5zM2.25 10.5a8.25 8.25 0 1114.59 5.28l4.69 4.69a.75.75 0 11-1.06 1.06l-4.69-4.69A8.25 8.25 0 012.25 10.5zm4.5 0a.75.75 0 01.75-.75h6a.75.75 0 010 1.5h-6a.75.75 0 01-.75-.75z" clipRule="evenodd" />
                  </svg>
                </button>
                <button
                  onClick={zoomIn}
                  className="p-2 rounded-md hover:bg-gray-600 transition-colors"
                  aria-label="Zoom in"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path fillRule="evenodd" d="M10.5 3.75a6.75 6.75 0 100 13.5 6.75 6.75 0 000-13.5zM2.25 10.5a8.25 8.25 0 1114.59 5.28l4.69 4.69a.75.75 0 11-1.06 1.06l-4.69-4.69A8.25 8.25 0 012.25 10.5zm4.5 0a.75.75 0 01.75-.75h2.25V7.5a.75.75 0 011.5 0v2.25H13.5a.75.75 0 010 1.5h-2.25v2.25a.75.75 0 01-1.5 0V11.25H7.5a.75.75 0 01-.75-.75z" clipRule="evenodd" />
                  </svg>
                </button>
                <span className="text-sm text-gray-300">
                  {Math.round(scale * 100)}%
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-sm text-gray-300">
                  Page {currentPage} of {numPages}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
});

export default PDFViewer;
